import type {
  ContentBlock,
  ContentBlockId,
  DataPointBody,
  DataPointSampleType,
  TestBody,
  TestMarkerCategory,
} from "./types";
import { getBlocksByKind, getRelatedBlocks } from "./registry";

export type BiomarkerTestingMethod = {
  testId: ContentBlockId;
  title: string;
  subtitle?: string;
  sampleTypes: DataPointSampleType[];
  collectionLabel: string;
  timing?: string;
  metricKey?: string;
};

const SAMPLE_TYPE_LABELS: Record<DataPointSampleType, string> = {
  blood: "Blood",
  saliva: "Saliva",
  urine: "Urine",
  semen: "Semen",
  ultrasound: "Ultrasound",
  imaging: "Imaging",
  genetic: "Genetic",
  swab: "Swab",
  tissue: "Tissue",
};

function normalizeEntityName(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

function inferSampleTypesFromCategory(
  category?: TestMarkerCategory,
): DataPointSampleType[] {
  switch (category) {
    case "blood-marker":
      return ["blood"];
    case "semen":
      return ["semen"];
    case "genetic":
      return ["blood", "saliva"];
    case "imaging":
    case "monitoring":
      return ["ultrasound"];
    default:
      return [];
  }
}

export function getCollectionLabel(body: TestBody): string {
  if (body.atHome && body.clinicRequired) return "At home or clinic";
  if (body.atHome && body.labAvailable) return "At home or lab";
  if (body.atHome) return "At home";
  if (body.labAvailable && body.clinicRequired) return "Lab or clinic";
  if (body.labAvailable) return "Lab";
  if (body.clinicRequired) return "Clinic";
  if (body.category === "blood-marker") return "Lab or clinic";
  if (body.category === "genetic") return "Lab, clinic, or kit";
  return "Care team";
}

function dataPointNames(block: ContentBlock<"data-point">): string[] {
  const body = block.body as DataPointBody;
  return [block.title, ...(body.aliases ?? []), body.metricKey ?? ""].filter(
    Boolean,
  );
}

export function getSampleTypeLabel(sampleType: DataPointSampleType): string {
  return SAMPLE_TYPE_LABELS[sampleType];
}

export function getDataPointForMarkerName(
  markerName: string,
): ContentBlock<"data-point"> | undefined {
  const query = normalizeEntityName(markerName);
  if (!query) return undefined;

  const dataPoints = getBlocksByKind("data-point");
  const exact = dataPoints.find((block) =>
    dataPointNames(block).some((name) => normalizeEntityName(name) === query),
  );
  if (exact) return exact;

  return dataPoints.find((block) =>
    dataPointNames(block).some((name) => {
      const normalized = normalizeEntityName(name);
      return normalized.includes(query) || query.includes(normalized);
    }),
  );
}

export function getTestsForDataPoint(
  dataPointId: ContentBlockId,
): ContentBlock<"test">[] {
  return getRelatedBlocks(dataPointId, "data-for")
    .map(({ block }) => block)
    .filter((block): block is ContentBlock<"test"> => block.kind === "test");
}

/**
 * Return the data points measured by a test or panel.
 *
 * `data-for` is the canonical relationship source. TestBody.dataPoints is
 * retained as authoring metadata during migration, but consumers must use
 * this resolver so panels and single-marker tests share one relationship path.
 */
export function getDataPointsForTest(
  testId: ContentBlockId,
): ContentBlock<"data-point">[] {
  return getRelatedBlocks(testId, "data-for")
    .map(({ block }) => block)
    .filter(
      (block): block is ContentBlock<"data-point"> =>
        block.kind === "data-point",
    );
}

export function getDataPointsForProduct(
  productId: ContentBlockId,
): ContentBlock<"data-point">[] {
  const product = getBlocksByKind("product").find(
    (block) => block.id === productId,
  );
  if (!product) return [];

  const includedDataPointIds = new Set(product.body.includedDataPointIds ?? []);
  return getBlocksByKind("data-point").filter((block) =>
    includedDataPointIds.has(block.id),
  );
}

export function getTestingMethodsForMarker(
  markerName: string,
): BiomarkerTestingMethod[] {
  const dataPoint = getDataPointForMarkerName(markerName);
  if (!dataPoint) return [];

  const body = dataPoint.body as DataPointBody;
  return getTestsForDataPoint(dataPoint.id).map((test) => {
    const testBody = test.body as TestBody;
    const sampleTypes =
      body.sampleTypes?.length || !testBody.category
        ? (body.sampleTypes ?? [])
        : inferSampleTypesFromCategory(testBody.category);

    return {
      testId: test.id,
      title: test.title,
      subtitle: test.subtitle,
      sampleTypes,
      collectionLabel: getCollectionLabel(testBody),
      timing: testBody.cycleDay,
      metricKey: body.metricKey,
    };
  });
}
